import GigShareDialog from "@/components/GigShareDialog";
import { useState, useEffect, useRef } from "react";
import { openExternalLink } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MapPin,
  Navigation,
  Car,
  Train,
  Users,
  Loader2,
  Share2,
  MessageCircle,
  Link as LinkIcon,
  X,
  ChevronDown,
  Check,
} from "lucide-react";
import { apiClient, API } from "@/App";
import { copyToClipboard } from "@/lib/clipboard";
import { gigContactsFor } from "@/lib/gigContacts";
import ContactSheet from "@/components/ContactSheet";
import { format, parseISO, isToday, isTomorrow } from "date-fns";
import { toast } from "sonner";
import { gigLocalDate } from "@/utils/dateUtils";
import { tokens } from "@/design-system/tokens";
import {
  Kicker,
  Dot,
  UrgencyPill,
  StatTile,
  Avatar,
} from "@/design-system/primitives";

// Aliases so existing dPal.X / dFont.X / dType.X references inside this
// file resolve directly to the design-system tokens. tokens.color is a
// strict superset of every key the file's old local dPal palette used.
const dPal    = tokens.color;
const dFont   = tokens.font;
const dType   = tokens.type;
const dRad    = tokens.radius;
const dShadow = tokens.shadow;
const dBorder = tokens.border;

// Highlights whichever of the trip's three date blocks (travel/perf/return)
// matches today, so it's clear which leg of a multi-day gig you're on.
// Flips automatically at midnight since it's just a date-string comparison.
// Mirrors GigBrief.jsx (mobile).
const TRIP_LEG_TODAY_BORDER = "#8FB8E8"; // pastel mid blue

// ─────────────────────────────────────────────────────────────────────
// Desktop variant of the Gig Brief card.
// Renders above ~900px. Composition: 6 horizontal bands instead of
// the mobile single-column stack. Same props & data contract as GigBrief.
// Reference design: gig-card-desktop.jsx (handoff folder).
// ─────────────────────────────────────────────────────────────────────

// Local atoms (Kicker, Dot, GhostBtn, UrgencyPill, StatTile, TransportToggle,
// Toggle, Avatar) used to live here. They now come from
// @/design-system/primitives — see the import block above. The dPal alias
// keeps existing dPal.X references valid.

// Has the traveller actually left for the airport yet? Calendar-day-only
// comparisons (today >= travelDate) flip phase at midnight on travel_date,
// hours before the real departure_time — so the brief would claim "Travelling
// to venue" while the user is still at home. When departure_time is known,
// the cutover is the real departure moment (travel_date + departure_time);
// only falls back to the midnight boundary when there's no time to anchor to.
// Mirrors the same helper in GigBrief.jsx (mobile) — keep both in sync.
function hasDepartedForAirport(gig) {
  if (!gig?.travel_date) return false;
  const travelDate = new Date(gig.travel_date + "T00:00:00");
  if (!gig.departure_time) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= travelDate;
  }
  const [dh, dm] = gig.departure_time.split(":").map(Number);
  const departureMoment = new Date(travelDate);
  departureMoment.setHours(dh, dm, 0, 0);
  return new Date() >= departureMoment;
}

// Same idea, for the return leg (return_date + return_time). Mirrors
// GigBrief.jsx (mobile).
function hasDepartedForReturn(gig) {
  if (!gig?.return_date) return false;
  const returnDate = new Date(gig.return_date + "T00:00:00");
  if (!gig.return_time) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= returnDate;
  }
  const [rh, rm] = gig.return_time.split(":").map(Number);
  const returnMoment = new Date(returnDate);
  returnMoment.setHours(rh, rm, 0, 0);
  return new Date() >= returnMoment;
}

// Soonest schedule row still ahead of right now. Mirrors GigBrief.jsx (mobile).
// Optional labelFilter narrows to a specific kind of row (e.g. "airport
// transfer"). Mirrors GigBrief.jsx (mobile).
function nextUpcomingScheduleItem(schedule, labelFilter) {
  const nowHHMM = format(new Date(), "HH:mm");
  const upcoming = (schedule || [])
    .filter((item) => item.time && item.time > nowHHMM)
    .filter((item) => !labelFilter || labelFilter(item.label || ""))
    .sort((a, b) => a.time.localeCompare(b.time));
  return upcoming[0] || null;
}

const isAirportTransferLabel = (label) => /transfer/i.test(label);

// Ambient "you're here, doing the thing" status — see GigBrief.jsx (mobile)
// for the full rationale.
function gigActivityLabel(gig) {
  const venue = gig?.venue_name || "the venue";
  if (gig?.gig_type === "rehearsal") return `Rehearsing at ${venue}`;
  if (gig?.gig_type === "session") return `At ${venue}`;
  return `Playing at ${venue}`;
}

// ───── Main component ───────────────────────────────────────────────

export const GigBriefDesktop = ({ gig, followingGig, collaborators = [], onHide }) => {
  // Notes now live in gig_notes; fall back to the legacy description field until re-saved
  const notesOf = (g) => g?.gig_notes ?? g?.description ?? "";
  const [journeyInfo, setJourneyInfo] = useState(null);
  const [loadingJourney, setLoadingJourney] = useState(false);
  const [transportMode, setTransportMode] = useState("driving");
  const [applyBuffer, setApplyBuffer] = useState(true);
  const [shareEnabled, setShareEnabled] = useState(gig?.share_enabled || false);
  const [activeContact, setActiveContact] = useState(null);
  const [shareToken, setShareToken] = useState(gig?.share_token || null);
  const [shareKind, setShareKind] = useState(null); // "family" | "dep" | null
  const [shareToggling, setShareToggling] = useState(false);
  const [dressCodeOpen, setDressCodeOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [finishingTrip, setFinishingTrip] = useState(false);

  const [editingField, setEditingField] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editValues, setEditValues] = useState({
    call_time:   gig?.call_time   || "",
    start_time:  gig?.start_time  || "",
    fee:         gig?.fee != null ? String(gig.fee) : "",
    dress_code:  gig?.dress_code  || "",
    gig_notes: gig?.gig_notes ?? gig?.description ?? "",
  });
  // Esc-to-cancel: set this flag, then blur the input. The blur handler
  // checks it and bails out of save.
  const cancelEditRef = useRef(false);

  // Multi-day next action state
  const [nextAction, setNextAction] = useState(null);
  const [nextActionLeaveByTime, setNextActionLeaveByTime] = useState(null);
  // True when nextAction/nextActionLeaveByTime came from the gig's own
  // Schedule rather than a computed travel phase — carries its own fixed
  // time, so the live directions fetch below skips it entirely.
  const [nextActionFromSchedule, setNextActionFromSchedule] = useState(false);
  const [loadingNextActionTime, setLoadingNextActionTime] = useState(false);
  // Why a live leave-by couldn't be computed (e.g. no home address) — shown
  // in place of the time so the failure isn't silent.
  const [nextActionLeaveByReason, setNextActionLeaveByReason] = useState(null);
  // Which travel_checkpoints key (if any) the CURRENT next action maps to —
  // see GigBrief.jsx (mobile) for the full rationale.
  const [nextActionCheckpoint, setNextActionCheckpoint] = useState(null);
  const [completingCheckpoint, setCompletingCheckpoint] = useState(false);
  // True for the ambient "Rehearsing/Playing at X" status — nothing to route
  // or tick, so the live leave-by fetch below skips it entirely.
  const [nextActionIsActivity, setNextActionIsActivity] = useState(false);

  useEffect(() => {
    // Load saved buffer preference; if the gig doesn't specify its own
    // transport, also default the mode toggle to the user's saved transport
    // preference instead of always "driving".
    (async () => {
      try {
        const res = await apiClient.get("/user/travel-settings");
        if (res.data?.apply_buffer_in_dashboard != null) {
          setApplyBuffer(!!res.data.apply_buffer_in_dashboard);
        }
        if (!gig?.transport_type && res.data?.default_transport_mode === "transit") {
          setTransportMode("transit");
        }
      } catch {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The gig's own Transport field wins over the settings default (see
  // GigBrief.jsx for the full rationale).
  useEffect(() => {
    if (gig?.transport_type === "public_transport") setTransportMode("transit");
    else if (gig?.transport_type === "driving") setTransportMode("driving");
    else if (gig?.transport_type === "pickup") setTransportMode(gig?.is_multiday ? "transfer" : "driving");
  }, [gig?.gig_id, gig?.transport_type, gig?.is_multiday]);

  useEffect(() => {
    if (gig?.call_time) fetchJourneyTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gig?.gig_id, transportMode, applyBuffer]);

  // Compute next action whenever the relevant gig fields change
  useEffect(() => {
    computeNextAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gig?.gig_id, gig?.is_multiday, gig?.travel_date, gig?.date, gig?.departure_time, gig?.airport_buffer, gig?.call_time, gig?.venue_name, gig?.return_date, gig?.return_time, gig?.destination_airport]);

  // Fetch live leave-by time when the action or transport mode changes
  useEffect(() => {
    if (nextAction) fetchNextActionLeaveByTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextAction, transportMode, applyBuffer, gig?.gig_id]);

  // Sequence-guarded: only the newest request may write state, so a slow
  // transit (station-timetable) response can't land after a later driving
  // response and leave a train row showing against the car toggle. See
  // GigBrief.jsx for the full rationale.
  const journeyReqSeq = useRef(0);
  const fetchJourneyTime = async () => {
    if (!gig?.call_time) return;
    const seq = ++journeyReqSeq.current;
    setLoadingJourney(true);
    try {
      const res = await apiClient.post("/travel/journey-time", {
        gig_id: gig.gig_id,
        transport_mode: transportMode,
        apply_buffer: applyBuffer,
      });
      if (seq !== journeyReqSeq.current) return; // superseded by a newer toggle
      setJourneyInfo(res.data);
    } catch (e) {
      console.error("Failed to fetch journey time:", e);
      if (seq === journeyReqSeq.current) {
        setJourneyInfo({ success: false, error: "Couldn't fetch travel time — try again" });
      }
    } finally {
      if (seq === journeyReqSeq.current) setLoadingJourney(false);
    }
  };

  const toggleBuffer = async () => {
    const next = !applyBuffer;
    setApplyBuffer(next);
    try {
      await apiClient.put("/user/travel-settings", { apply_buffer_in_dashboard: next });
    } catch (err) {
      console.error("Failed to save buffer preference:", err);
    }
  };

  // Check if performance has started (used to show GIG FINISHED button)
  const hasPerformanceStarted = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const performanceDate = gig?.is_multiday
      ? (gig?.date ? new Date(gig.date + "T00:00:00") : null)
      : (gig?.date ? new Date(gig.date + "T00:00:00") : null);

    return performanceDate && today.getTime() >= performanceDate.getTime();
  };

  // Compute next action for multi-day gigs (mirrors GigBrief.jsx mobile)
  const computeNextAction = () => {
    // Reset once up front — only the "on-site activity" branch below sets this
    // back to true, for this same synchronous call, when it's actually reached.
    setNextActionIsActivity(false);

    if (!gig?.is_multiday) {
      setNextAction(null);
      setNextActionLeaveByTime(null);
      setNextActionFromSchedule(false);
      setNextActionCheckpoint(null);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const travelDate = gig.travel_date ? new Date(gig.travel_date + "T00:00:00") : null;
    const perfDate = gig.date ? new Date(gig.date + "T00:00:00") : null;

    if (!travelDate || !perfDate) {
      setNextAction(null);
      setNextActionLeaveByTime(null);
      setNextActionFromSchedule(false);
      setNextActionCheckpoint(null);
      return;
    }

    const departureAirport = gig.departure_airport?.name || "airport";

    // Manually-ticked checkpoints (the "Done" button below) take priority
    // over the time-based guesses — see GigBrief.jsx (mobile) for the full
    // rationale (this used to leave "Leave for X" showing an already-elapsed
    // leave-by time all the way until the literal flight/departure moment).
    const checkpoints = gig.travel_checkpoints || {};

    // Not departed yet (before travel_date, or on travel_date but still
    // ahead of departure_time — or not yet manually ticked): "Leave for
    // [departure_airport]"
    if (!checkpoints.departed_airport && !hasDepartedForAirport(gig)) {
      let leaveBy = null;
      if (gig.departure_time && gig.airport_buffer) {
        const [h, m] = gig.departure_time.split(":").map(Number);
        const leaveMinutes = h * 60 + m - gig.airport_buffer;
        if (leaveMinutes >= 0) {
          leaveBy = `${String(Math.floor(leaveMinutes / 60)).padStart(2, "0")}:${String(leaveMinutes % 60).padStart(2, "0")}`;
        }
      }
      setNextAction(`Leave for ${departureAirport}`);
      setNextActionLeaveByTime(leaveBy);
      setNextActionFromSchedule(false);
      setNextActionCheckpoint("departed_airport");
      return;
    }

    // Departed, but before perf_date: "Travelling to venue"
    if (today < perfDate) {
      setNextAction("Travelling to venue");
      setNextActionLeaveByTime(null);
      setNextActionFromSchedule(false);
      setNextActionCheckpoint(null);
      return;
    }

    // On performance date: the day's own Schedule (running order) beats a
    // recomputed "Leave for venue" — once you're on site, a fixed pickup/
    // soundcheck time is more reliable than a fresh drive-from-home estimate.
    // Also reachable via a manual "arrived_venue" tick even while today still
    // equals perfDate — without that, a SAME-DAY return leg could never
    // surface (see GigBrief.jsx mobile for the full rationale).
    if (!checkpoints.arrived_venue && today.getTime() === perfDate.getTime()) {
      const scheduleItem = nextUpcomingScheduleItem(gig.schedule);
      if (scheduleItem) {
        setNextAction(scheduleItem.label || "Next up");
        setNextActionLeaveByTime(scheduleItem.time);
        setNextActionFromSchedule(true);
        setNextActionCheckpoint(null);
        return;
      }

      setNextAction(`Leave for ${gig.venue_name || "venue"}`);
      let leaveBy = null;
      if (gig.call_time) {
        const [h, m] = gig.call_time.split(":").map(Number);
        const leaveMinutes = h * 60 + m - 5;
        if (leaveMinutes >= 0) {
          leaveBy = `${String(Math.floor(leaveMinutes / 60)).padStart(2, "0")}:${String(leaveMinutes % 60).padStart(2, "0")}`;
        }
      }
      setNextActionLeaveByTime(leaveBy);
      setNextActionFromSchedule(false);
      setNextActionCheckpoint("arrived_venue");
      return;
    }

    // You've arrived. Keep following the day's own Schedule exactly as
    // before arrival (see GigBrief.jsx mobile for the full rationale).
    const onSiteScheduleItem = nextUpcomingScheduleItem(gig.schedule);
    if (onSiteScheduleItem) {
      setNextAction(onSiteScheduleItem.label || "Next up");
      setNextActionLeaveByTime(onSiteScheduleItem.time);
      setNextActionFromSchedule(true);
      setNextActionCheckpoint(null);
      return;
    }

    // Nothing left on the Schedule: an ambient "you're here, doing the thing"
    // status for as long as the gig is still plausibly running (see
    // GigBrief.jsx mobile for the full rationale).
    const stillOnSite = (() => {
      const now = new Date();
      if (gig.end_time) {
        const [eh, em] = gig.end_time.split(":").map(Number);
        const endMoment = new Date(perfDate);
        endMoment.setHours(eh, em, 0, 0);
        return now < endMoment;
      }
      const anchorTime = gig.call_time || gig.start_time;
      if (!anchorTime) return false;
      const [ah, am] = anchorTime.split(":").map(Number);
      const endMoment = new Date(perfDate);
      endMoment.setHours(ah, am, 0, 0);
      endMoment.setHours(endMoment.getHours() + 4);
      return now < endMoment;
    })();

    if (stillOnSite) {
      setNextAction(gigActivityLabel(gig));
      setNextActionLeaveByTime(null);
      setNextActionFromSchedule(false);
      setNextActionIsActivity(true);
      setNextActionCheckpoint(null);
      return;
    }

    // After the performance: heading home. Mirrors the outbound leg, but
    // uses return_date/return_time/destination_airport (the venue-side
    // airport you're flying out FROM) — never a live directions fetch here,
    // since you're not at home to compute a drive from.
    if (gig.return_date) {
      const returnDate = new Date(gig.return_date + "T00:00:00");

      if (!checkpoints.departed_return && !hasDepartedForReturn(gig)) {
        // A scheduled "airport transfer" row beats the airport_buffer
        // estimate outright — someone's picking you up at a fixed time,
        // not a calculation.
        const transferItem = nextUpcomingScheduleItem(gig.schedule, isAirportTransferLabel);
        if (transferItem) {
          setNextAction(transferItem.label || "Airport transfer");
          setNextActionLeaveByTime(transferItem.time);
          setNextActionFromSchedule(true);
          setNextActionCheckpoint(null);
          return;
        }

        const returnAirport = gig.destination_airport?.name || "airport";
        let leaveBy = null;
        if (gig.return_time && gig.airport_buffer) {
          const [h, m] = gig.return_time.split(":").map(Number);
          const leaveMinutes = h * 60 + m - gig.airport_buffer;
          if (leaveMinutes >= 0) {
            leaveBy = `${String(Math.floor(leaveMinutes / 60)).padStart(2, "0")}:${String(leaveMinutes % 60).padStart(2, "0")}`;
          }
        }
        setNextAction(`Leave for ${returnAirport}`);
        setNextActionLeaveByTime(leaveBy);
        setNextActionFromSchedule(false);
        setNextActionCheckpoint("departed_return");
        return;
      }

      if (today <= returnDate) {
        setNextAction("Travelling home");
        setNextActionLeaveByTime(null);
        setNextActionFromSchedule(false);
        setNextActionCheckpoint(null);
        return;
      }
    }

    // After the return leg (or no return leg was ever set): trip complete
    setNextAction(null);
    setNextActionLeaveByTime(null);
    setNextActionFromSchedule(false);
    setNextActionCheckpoint(null);
  };

  // Tick a travel checkpoint and advance the brief immediately (mirrors
  // GigBrief.jsx mobile — see there for the full rationale).
  const handleCompleteCheckpoint = async (checkpoint) => {
    if (!checkpoint) return;
    setCompletingCheckpoint(true);
    try {
      const merged = { ...(gig.travel_checkpoints || {}), [checkpoint]: new Date().toISOString() };
      await apiClient.put(`/gigs/${gig.gig_id}`, { travel_checkpoints: merged });
      Object.assign(gig, { travel_checkpoints: merged });
      computeNextAction();
    } catch (error) {
      toast.error("Failed to update");
      console.error(error);
    } finally {
      setCompletingCheckpoint(false);
    }
  };

  // Fetch live leave-by time from Directions API for next action.
  // Sequence-guarded like fetchJourneyTime: only the newest request may
  // write state, so a slow transit response can't overwrite a later one.
  const nextActionReqSeq = useRef(0);
  const fetchNextActionLeaveByTime = async () => {
    if (!gig?.is_multiday || !nextAction) return;
    // Schedule-sourced actions already carry their own fixed time (see
    // computeNextAction) — there's no route to recompute.
    if (nextActionFromSchedule) return;
    // Transfer means someone else is driving — there's nothing to route, so
    // keep the static buffer-based estimate computeNextAction already set.
    if (transportMode === 'transfer') return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const travelDate = gig.travel_date ? new Date(gig.travel_date + "T00:00:00") : null;
    const perfDate = gig.date ? new Date(gig.date + "T00:00:00") : null;

    if (!travelDate || !perfDate) return;

    // After the performance: heading home. Same static-only treatment as
    // computeNextAction's return-leg branch — no live fetch, since you're
    // not at home to route a drive from.
    if (today > perfDate) return;

    const seq = ++nextActionReqSeq.current;
    setLoadingNextActionTime(true);
    setNextActionLeaveByReason(null);
    const applyResponse = (res) => {
      if (seq !== nextActionReqSeq.current) return; // superseded by a newer toggle
      if (res.data?.success && res.data?.leave_by_time) {
        setNextActionLeaveByTime(res.data.leave_by_time);
      } else if (res.data?.error) {
        setNextActionLeaveByReason(res.data.error);
      }
    };
    try {
      // Ambient "Rehearsing/Playing at X" status — nothing to route while
      // you're on site (see GigBrief.jsx mobile for the full rationale).
      if (nextActionIsActivity) return;

      // Not departed yet: home → departure_airport. Buffer is optional — the
      // backend falls back to the user's default buffer when it's omitted.
      if (!hasDepartedForAirport(gig) && gig.departure_time) {
        const payload = {
          gig_id: gig.gig_id,
          transport_mode: transportMode,
          apply_buffer: applyBuffer,
          destination_type: "airport",
          destination_airport: gig.departure_airport?.name || "airport",
          reference_time: gig.departure_time,
        };
        if (gig.airport_buffer) payload.buffer_minutes = gig.airport_buffer;
        applyResponse(await apiClient.post("/travel/journey-time", payload));
        return;
      }

      // On performance date: current location → venue
      if (today.getTime() === perfDate.getTime() && gig.call_time) {
        applyResponse(await apiClient.post("/travel/journey-time", {
          gig_id: gig.gig_id,
          transport_mode: transportMode,
          apply_buffer: applyBuffer,
          destination_type: "venue",
          reference_time: gig.call_time,
          buffer_minutes: 5,
        }));
        return;
      }
    } catch (error) {
      console.error("Failed to fetch next action leave-by time:", error);
    } finally {
      if (seq === nextActionReqSeq.current) setLoadingNextActionTime(false);
    }
  };

  if (!gig) return null;

  // For multi-day gigs the headline date is the travel/away start, not the
  // performance date. Domestic gigs continue to use gig.date.
  const displayDateStr = gig.is_multiday && gig.travel_date ? gig.travel_date : gig.date;
  const gigDate = displayDateStr ? parseISO(displayDateStr) : null;
  // Guard: if gig has no valid date, bail out gracefully rather than letting
  // format() throw "Invalid time value" downstream and blank the dashboard.
  if (!gigDate || isNaN(gigDate.getTime())) return null;

  const gigDayStr = gigLocalDate(displayDateStr);
  const todayStr = new Date().toLocaleDateString("en-CA");
  const daysUntil = Math.round(
    (new Date(gigDayStr + "T00:00:00") - new Date(todayStr + "T00:00:00")) / 86400000
  );

  // Once we're past this gig's own performance date — i.e. on the return
  // leg — the urgency badge switches to the NEXT actual gig instead of
  // showing a negative day count for a trip that's already happened. The
  // brief body below still shows this gig's travel-day details unchanged.
  // Mirrors GigBrief.jsx (mobile).
  const perfDayStr = gig.date ? gigLocalDate(gig.date) : null;
  const pastPerformance = gig.is_multiday && perfDayStr && todayStr > perfDayStr;
  let badgeDate = gigDate;
  let badgeDaysUntil = daysUntil;
  if (pastPerformance) {
    const followingDateStr = followingGig?.date ? gigLocalDate(followingGig.date) : null;
    const followingDate = followingDateStr ? parseISO(followingDateStr) : null;
    if (followingDate && !isNaN(followingDate.getTime())) {
      badgeDate = followingDate;
      badgeDaysUntil = Math.round(
        (new Date(followingDateStr + "T00:00:00") - new Date(todayStr + "T00:00:00")) / 86400000
      );
    } else {
      badgeDate = null;
    }
  }

  // ── Share / nav handlers (same surface as GigBrief.jsx) ──
  const apiBase = API.startsWith("http") ? API : window.location.origin + API;
  const briefShareUrl = shareToken ? `${apiBase}/share/gig/${shareToken}/og` : null;

  const handleShareToggle = async () => {
    setShareToggling(true);
    try {
      const res = await apiClient.put(`/gigs/${gig.gig_id}/share`);
      setShareEnabled(res.data.share_enabled);
      setShareToken(res.data.share_token);
      toast.success(res.data.share_enabled ? "Sharing enabled" : "Sharing disabled");
    } catch {
      toast.error("Failed to toggle sharing");
    } finally {
      setShareToggling(false);
    }
  };

  const handleShare = async (platform) => {
    if (platform === "copy" && briefShareUrl) {
      await copyToClipboard(briefShareUrl);
      toast.success("Link copied!");
    }
  };

  // Phase-aware nav target: before travel → departure airport, after the gig →
  // return airport, otherwise null (route to venue). Reuses the same date logic
  // that drives nextAction so the Directions button matches the brief's phase.
  // The return leg departs from destination_airport (where the outbound
  // flight landed, near the venue — e.g. Marseille), NOT return_airport
  // (where the return flight lands, back home — e.g. Heathrow).
  function resolveNavAirport() {
    if (!gig?.is_multiday) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const travelDate = gig.travel_date ? new Date(gig.travel_date + "T00:00:00") : null;
    const perfDate = gig.date ? new Date(gig.date + "T00:00:00") : null;
    if (!travelDate || !perfDate) return null;
    if (!hasDepartedForAirport(gig)) return gig.departure_airport || null;
    // Once the return flight has actually departed, there's nothing left to
    // navigate to — without this check, the button kept pointing back at
    // the venue-side airport for the rest of the return day even after
    // landing back home.
    if (today > perfDate && !hasDepartedForReturn(gig)) return gig.destination_airport || gig.departure_airport || null;
    return null; // travel window / gig day / post-return → venue
  }

  // Citymapper's /directions link treats endcoord as the destination proper;
  // endname/endaddress are display labels only — see GigBrief.jsx for the
  // full rationale (their address-only geocoding stopped working July 2026).
  const citymapperUrl = ({ lat, lng, name, address }) => {
    const params = [];
    if (lat != null && lng != null) params.push(`endcoord=${lat},${lng}`);
    if (name) params.push(`endname=${encodeURIComponent(name)}`);
    if (address) params.push(`endaddress=${encodeURIComponent(address)}`);
    return `https://citymapper.com/directions?${params.join("&")}`;
  };

  const openNavigation = (provider) => {
    // Airport phase: route by airport name (+ place_id where Maps supports it).
    const airport = resolveNavAirport();
    if (airport?.name) {
      const enc = encodeURIComponent(airport.name);
      const urls = {
        google: `https://www.google.com/maps/dir/?api=1&destination=${enc}${airport.place_id ? `&destination_place_id=${airport.place_id}` : ""}`,
        waze: `https://waze.com/ul?q=${enc}&navigate=yes`,
        citymapper: citymapperUrl({ name: airport.name, address: airport.address || airport.name }),
      };
      openExternalLink(urls[provider]);
      return;
    }
    // Not in the airport phase: route to the venue (shared with the Venue
    // card's own Directions button, so there's one venue-routing code path).
    openVenueNavigation(provider);
  };

  // Always route to the VENUE regardless of trip phase, by address/name text
  // for Google/Waze — never cached venue_lat/venue_lng there. A stale or
  // wrong cached coordinate (e.g. from an earlier bad geocode) would silently
  // win over a correct address every time, which is exactly how this sent
  // people to the wrong place; address-only search has no such cache to go
  // stale. Citymapper is the exception — see citymapperUrl above.
  const openVenueNavigation = (provider) => {
    const venueAddress = gig.venue_address
      ? `${gig.venue_name}, ${gig.venue_address}`
      : `${gig.venue_name}, ${gig.city}`;
    if (!venueAddress) return;
    const enc = encodeURIComponent(venueAddress);
    const urls = {
      google: `https://www.google.com/maps/search/?api=1&query=${enc}`,
      waze: `https://waze.com/ul?q=${enc}&navigate=yes`,
      citymapper: citymapperUrl({ lat: gig.venue_lat, lng: gig.venue_lng, name: gig.venue_name, address: venueAddress }),
    };
    openExternalLink(urls[provider]);
  };

  // Route to the HOTEL by its address (or name as a fallback). Independent of
  // trip phase — the Hotel card always points at the accommodation.
  const openHotelNavigation = (provider) => {
    const hotelAddress = gig.accommodation?.address || gig.accommodation?.name;
    if (!hotelAddress) return;
    const enc = encodeURIComponent(hotelAddress);
    const urls = {
      google: `https://www.google.com/maps/search/?api=1&query=${enc}`,
      waze: `https://waze.com/ul?q=${enc}&navigate=yes`,
      citymapper: citymapperUrl({ name: gig.accommodation?.name, address: hotelAddress }),
    };
    openExternalLink(urls[provider]);
  };

  // ── Edit handlers ──
  // Coerce the draft value to the right server-side type. fee is the only
  // numeric field; the rest are strings (or empty -> null).
  const coerceForSave = (field, raw) => {
    if (field === "fee") {
      if (raw === "" || raw == null) return null;
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : null;
    }
    return raw === "" ? null : raw;
  };

  // Skip the API call when the draft equals the current value — avoids a
  // toast on every accidental blur. Empty/null are treated as equivalent.
  const isUnchanged = (field, raw) => {
    const current = gig?.[field];
    if ((current == null || current === "") && (raw == null || raw === "")) return true;
    if (field === "fee") return String(current ?? "") === String(raw ?? "");
    return String(current ?? "") === String(raw ?? "");
  };

  const handleSaveEdit = async (field) => {
    const raw = editValues[field];
    if (isUnchanged(field, raw)) {
      setEditingField(null);
      return;
    }
    setIsSaving(true);
    try {
      const value = coerceForSave(field, raw);
      const updates = { [field]: value };
      if (field === "gig_notes") updates.description = null; // legacy field, now unused
      await apiClient.put(`/gigs/${gig.gig_id}`, updates);
      Object.assign(gig, updates);
      toast.success("Updated");
      setEditingField(null);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditValues({
      call_time:   gig?.call_time   || "",
      start_time:  gig?.start_time  || "",
      fee:         gig?.fee != null ? String(gig.fee) : "",
      dress_code:  gig?.dress_code  || "",
      gig_notes: gig?.gig_notes ?? gig?.description ?? "",
    });
    setEditingField(null);
  };

  // Common keyDown/blur wiring for the desktop inline editors. Enter blurs
  // (which triggers save via onBlur); Escape sets the cancel flag and blurs
  // Multi-day "Trip done": mark the trip complete so the next-action box
  // stops surfacing prompts. Mirrors handleSaveEdit (persist, then mutate).
  const toggleTripDone = async () => {
    const next = !gig.gig_finished;
    setFinishingTrip(true);
    try {
      await apiClient.put(`/gigs/${gig.gig_id}`, { gig_finished: next });
      Object.assign(gig, { gig_finished: next });
      toast.success(next ? "Trip marked done" : "Trip reopened");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update");
    } finally {
      setFinishingTrip(false);
    }
  };

  // (the blur handler reads the flag and skips save).
  const inlineEditHandlers = (field) => ({
    onBlur: () => {
      if (cancelEditRef.current) {
        cancelEditRef.current = false;
        handleCancelEdit();
      } else {
        handleSaveEdit(field);
      }
    },
    onKeyDown: (e) => {
      if (e.key === "Escape") {
        cancelEditRef.current = true;
        e.currentTarget.blur();
      } else if (e.key === "Enter" && e.currentTarget.tagName !== "TEXTAREA") {
        e.currentTarget.blur();
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        // Cmd/Ctrl-Enter in the notes textarea commits without inserting a newline
        e.currentTarget.blur();
      }
    },
  });

  const gigCollaborators = gigContactsFor(gig, collaborators, notesOf(gig));

  // Single-line readability: render Dress code / Notes / Gig contacts inline so
  // they're legible at a glance, and only fall back to a collapsible dropdown
  // when the content is too long (notes) or too numerous (contacts) for one line.
  const notesText = notesOf(gig) || "";
  const notesFitInline = notesText.length <= 48 && !notesText.includes("\n");
  const dressText = gig.dress_code || "";
  const dressFitInline = dressText.length <= 48 && !dressText.includes("\n");
  const contactsFitInline = gigCollaborators.length <= 2;

  // ── Date / time display values ──
  const dateYear  = format(gigDate, "yyyy");
  const dateUpper = format(gigDate, "EEE d MMM yyyy").toUpperCase();

  // Directions label reflects the routing destination (airport / venue / default)
  const navAirport = resolveNavAirport();
  const directionsLabel = navAirport
    ? "Get to airport"
    : (gig.is_multiday && nextAction ? "Get to venue" : "Directions");

  // ─────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1180,
        margin: "0 auto",
        background: dPal.cardBg,
        border: dBorder.card,
        borderRadius: dRad.xxl,
        overflow: "hidden",
        boxShadow: dShadow.cardLg,
        fontFamily: dFont.body,
        color: dPal.text,
      }}
      data-testid="gig-brief-desktop"
    >
      {/* ── 1. Top bar ─────────────────────────────────────────────── */}
      <div
        style={{
          padding: "20px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          borderBottom: dBorder.hairline,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flexWrap: "wrap" }}>
          <Kicker size={13}>Next gig</Kicker>
          <Dot />
          <UrgencyPill
            daysUntil={badgeDaysUntil}
            isToday={badgeDate ? isToday(badgeDate) : false}
            isTomorrow={badgeDate ? isTomorrow(badgeDate) : false}
          />
          <Dot />
          <Kicker size={12} color={dPal.textDim}>
            {dateUpper}
          </Kicker>
          {/* When the badge has switched to the next gig (this one's on its
              return leg), name it — otherwise "5 days" reads as if it's
              about the gig shown below. Truncates rather than pushing the
              row wider than the card. */}
          {pastPerformance && badgeDate && followingGig?.artist_name && (
            <>
              <Dot />
              <Kicker
                size={12}
                color={dPal.textDim}
                style={{ minWidth: 0, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {followingGig.artist_name}
              </Kicker>
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {onHide && (
            <button
              type="button"
              onClick={onHide}
              aria-label="Hide widget"
              title="Hide widget"
              data-testid="gig-brief-hide"
              style={{
                width: 32,
                height: 32,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                color: dPal.textMuted,
                border: "none",
                borderRadius: dRad.md,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* ── 2. Hero (artist) ───────────────────────────────────────── */}
      <div
        style={{
          padding: "40px 32px 36px",
          textAlign: "center",
          borderBottom: dBorder.hairline,
        }}
      >
        <h1
          style={{
            margin: 0,
            ...dType.hero,
            color: dPal.text,
            textWrap: "balance",
          }}
        >
          {gig.artist_name}
        </h1>
      </div>

      {/* ── 3. Three-block date header (Part A) ──────────────────────── */}
      {gig.is_multiday ? (
        <>
          {/* Multi-day: 3 equal blocks (travel, gig, return) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gig.return_date ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
              gap: 16,
              padding: "24px 32px",
              borderBottom: dBorder.hairline,
              background: dPal.appBg,
            }}
          >
            {/* Block 1: Travel date */}
            <div
              style={{
                padding: "18px 16px",
                background: dPal.cardBg,
                border: gig.travel_date && gigLocalDate(gig.travel_date) === todayStr ? `2px solid ${TRIP_LEG_TODAY_BORDER}` : dBorder.hairline,
                borderRadius: dRad.md,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div style={{ fontSize: "22px" }}>✈️</div>
              <div style={{ fontSize: "13px", fontWeight: 600, color: dPal.text, lineHeight: 1.3 }}>
                {gig.travel_date ? format(parseISO(gig.travel_date), "EEE, MMM d") : "—"}
              </div>
            </div>

            {/* Block 2: Gig date */}
            <div
              style={{
                padding: "18px 16px",
                background: dPal.cardBg,
                border: gig.date && gigLocalDate(gig.date) === todayStr ? `2px solid ${TRIP_LEG_TODAY_BORDER}` : dBorder.hairline,
                borderRadius: dRad.md,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div style={{ fontSize: "22px" }}>🎵</div>
              <div style={{ fontSize: "13px", fontWeight: 600, color: dPal.text, lineHeight: 1.3 }}>
                {gig.date ? format(parseISO(gig.date), "EEE, MMM d") : "—"}
              </div>
            </div>

            {/* Block 3: Return date (show only if present) */}
            {gig.return_date && (
              <div
                style={{
                  padding: "18px 16px",
                  background: dPal.cardBg,
                  border: gigLocalDate(gig.return_date) === todayStr ? `2px solid ${TRIP_LEG_TODAY_BORDER}` : dBorder.hairline,
                  borderRadius: dRad.md,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div style={{ fontSize: "22px" }}>✈️</div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: dPal.text, lineHeight: 1.3 }}>
                  {format(parseISO(gig.return_date), "EEE, MMM d")}
                </div>
              </div>
            )}
          </div>

          {/* Next action box — hidden if gig_finished */}
          {!gig.gig_finished && (
          <div
            style={{
              padding: "20px 32px",
              borderBottom: dBorder.hairline,
            }}
          >
            {/* Action on the left, bigger controls grouped on the right */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 28, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <Kicker size={12} color={dPal.textDim}>Next action</Kicker>
                <div style={{ marginTop: 8, fontFamily: dFont.body, fontWeight: 600, fontSize: 22, lineHeight: 1.2, color: dPal.text }}>
                  {nextAction || "Trip complete"}
                </div>
              </div>

              {/* Controls — Transport | Depart | Directions, grouped and evenly gapped.
                  Transport toggle only makes sense when there's a route to
                  actually calculate — hidden for Schedule-sourced actions
                  (soundcheck, set times) and for Transfer (someone else is
                  driving), since neither has a travel time to toggle between. */}
              {nextAction && (nextActionLeaveByTime || nextActionLeaveByReason || loadingNextActionTime) && (
              <div style={{ display: "flex", alignItems: "center", gap: 28, flexShrink: 0 }}>
                {/* Transport toggle — sized up to match the brief's bigger controls */}
                {!nextActionFromSchedule && transportMode !== 'transfer' && (
                <div
                  style={{
                    display: "inline-flex",
                    gap: 4,
                    background: dPal.segBg,
                    border: dBorder.inner,
                    borderRadius: dRad.md + 2,
                    padding: 4,
                    flexShrink: 0,
                  }}
                >
                  {[
                    { id: "driving", icon: Car, label: "Drive" },
                    { id: "transit", icon: Train, label: "Transit" },
                    { id: "transfer", icon: Users, label: "Transfer" },
                  ].map((o) => {
                    const on = transportMode === o.id;
                    const Icon = o.icon;
                    return (
                      <button
                        key={o.id}
                        onClick={() => setTransportMode(o.id)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "10px 16px",
                          background: on ? dPal.segActive : "transparent",
                          color: on ? dPal.text : dPal.textMuted,
                          border: "none",
                          borderRadius: dRad.sm,
                          cursor: "pointer",
                          fontFamily: dFont.body,
                          fontSize: 14,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Icon className="h-4 w-4" />
                        {o.label}
                      </button>
                    );
                  })}
                </div>
                )}

                {/* Depart stat */}
                {loadingNextActionTime ? (
                  <Loader2 className="h-5 w-5 animate-spin" style={{ color: dPal.amber }} />
                ) : nextActionLeaveByTime ? (
                  <div style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                    <div style={{ fontFamily: dFont.body, fontWeight: 700, fontSize: 28, color: dPal.amber, letterSpacing: "-0.02em" }}>
                      {nextActionLeaveByTime}
                    </div>
                    <div style={{ marginTop: 4, fontFamily: dFont.mono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: dPal.textMuted }}>
                      Depart
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: dPal.textMuted, lineHeight: 1.35, maxWidth: 160 }}>
                    {nextActionLeaveByReason}
                  </div>
                )}

                {/* Directions button */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "14px 22px",
                        background: dPal.orange,
                        color: dPal.onOrange,
                        border: dBorder.brand,
                        borderRadius: dRad.md,
                        fontFamily: "inherit",
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      <Navigation className="h-4 w-4" />
                      {directionsLabel}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openNavigation("google")}>Google Maps</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openNavigation("waze")}>Waze</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openNavigation("citymapper")}>Citymapper</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Mark this step done — advances the brief immediately
                    instead of waiting for the time-based fallback to
                    eventually agree. Only shown for manually-tickable steps
                    (see nextActionCheckpoint in computeNextAction). */}
                {nextActionCheckpoint && (
                  <button
                    type="button"
                    onClick={() => handleCompleteCheckpoint(nextActionCheckpoint)}
                    disabled={completingCheckpoint}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "14px 22px",
                      background: "transparent",
                      color: dPal.textMuted,
                      border: dBorder.inner,
                      borderRadius: dRad.md,
                      fontFamily: "inherit",
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: completingCheckpoint ? "default" : "pointer",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                    data-testid="next-action-done-btn"
                  >
                    {completingCheckpoint ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Done
                  </button>
                )}
              </div>
              )}
            </div>
          </div>
          )}

        </>
      ) : null}

      {/* ── 4. Info row: Call time | Gig starts | Venue | Hotel ───────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 16,
          padding: "20px 32px",
          borderBottom: dBorder.hairline,
          background: dPal.appBg,
        }}
      >
        <EditableStatCell
          label="Call time"
          field="call_time"
          type="time"
          displayValue={gig.call_time || "—"}
          valueColor={gig.call_time ? dPal.amber : dPal.textMuted}
          editingField={editingField}
          editValues={editValues}
          setEditValues={setEditValues}
          isSaving={isSaving}
          inlineEditHandlers={inlineEditHandlers}
          onStartEdit={() => setEditingField("call_time")}
        />
        <EditableStatCell
          label="Gig starts"
          field="start_time"
          type="time"
          displayValue={gig.start_time || "TBD"}
          valueColor={gig.start_time ? dPal.text : dPal.textMuted}
          editingField={editingField}
          editValues={editValues}
          setEditValues={setEditValues}
          isSaving={isSaving}
          inlineEditHandlers={inlineEditHandlers}
          onStartEdit={() => setEditingField("start_time")}
        />

        {/* Venue card */}
        <div
          style={{
            padding: 16,
            background: dPal.cardBg,
            border: dBorder.hairline,
            borderRadius: dRad.md,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minWidth: 0,
          }}
        >
          <Kicker size={11} color={dPal.textDim}>VENUE</Kicker>
          <div style={{ marginTop: 10, fontWeight: 600, fontSize: 16, lineHeight: 1.3, color: dPal.text, overflowWrap: "break-word", marginBottom: 16 }}>
            {gig.venue_name || "—"}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  width: "100%",
                  padding: "8px 12px",
                  marginTop: "auto",
                  background: dPal.orange,
                  color: dPal.onOrange,
                  border: dBorder.brand,
                  borderRadius: dRad.md,
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <Navigation className="h-3.5 w-3.5" />
                Directions
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openVenueNavigation("google")}>Google Maps</DropdownMenuItem>
              <DropdownMenuItem onClick={() => openVenueNavigation("waze")}>Waze</DropdownMenuItem>
              <DropdownMenuItem onClick={() => openVenueNavigation("citymapper")}>Citymapper</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Hotel card (multi-day only) */}
        {gig.is_multiday && (
          <div
            style={{
              padding: 16,
              background: dPal.cardBg,
              border: dBorder.hairline,
              borderRadius: dRad.md,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minWidth: 0,
            }}
          >
            <Kicker size={11} color={dPal.textDim}>HOTEL</Kicker>
            <div style={{ marginTop: 10, fontWeight: 600, fontSize: 16, lineHeight: 1.3, color: dPal.text, overflowWrap: "break-word", marginBottom: 16 }}>
              {gig.accommodation?.name || "Hotel pending"}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    width: "100%",
                    padding: "8px 12px",
                    marginTop: "auto",
                    background: dPal.orange,
                    color: dPal.onOrange,
                    border: dBorder.brand,
                    borderRadius: dRad.md,
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <Navigation className="h-3.5 w-3.5" />
                  Directions
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openHotelNavigation("google")}>Google Maps</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openHotelNavigation("waze")}>Waze</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openHotelNavigation("citymapper")}>Citymapper</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Leave-by card (domestic only — multi-day uses the Next Action leave-by) */}
        {!gig.is_multiday && (
          <div
            style={{
              padding: 16,
              background: dPal.cardBg,
              border: dBorder.hairline,
              borderRadius: dRad.md,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minWidth: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <Kicker size={11} color={dPal.textDim}>LEAVE BY</Kicker>
              <div
                style={{
                  display: "inline-flex",
                  gap: 2,
                  backgroundColor: dPal.segBg,
                  borderRadius: dRad.sm,
                  padding: 2,
                }}
              >
                <button
                  onClick={() => setTransportMode("driving")}
                  style={{
                    width: 28,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: dRad.sm,
                    backgroundColor: transportMode === "driving" ? dPal.segActive : "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <Car className="h-3.5 w-3.5" style={{ color: transportMode === "driving" ? dPal.text : dPal.textMuted }} />
                </button>
                <button
                  onClick={() => setTransportMode("transit")}
                  style={{
                    width: 28,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: dRad.sm,
                    backgroundColor: transportMode === "transit" ? dPal.segActive : "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <Train className="h-3.5 w-3.5" style={{ color: transportMode === "transit" ? dPal.text : dPal.textMuted }} />
                </button>
              </div>
            </div>
            {loadingJourney ? (
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: dPal.textMuted }} />
            ) : journeyInfo?.success && journeyInfo?.leave_by_time ? (
              <>
                <div style={{ fontWeight: 700, fontSize: 20, color: dPal.amber, whiteSpace: "nowrap" }}>
                  {journeyInfo.leave_by_time}
                </div>
                <div style={{ fontSize: 12, color: dPal.textDim, marginTop: 6, lineHeight: 1.5 }}>
                  {journeyInfo.train_departure_time ? (
                    <>
                      <div>
                        🚗 {journeyInfo.drive_to_station_minutes}m drive
                        {journeyInfo.station_name ? ` to ${journeyInfo.station_name}` : ""}
                      </div>
                      {/* The timetable is for the GIG's date, not today — tag
                          the weekday when they differ so a Friday train checked
                          on a Tuesday doesn't look made-up. */}
                      <div>
                        🚂 {journeyInfo.train_departure_time} {journeyInfo.transit_vehicle || "train"}
                        {gig.date && !isToday(parseISO(gig.date)) ? ` (${format(parseISO(gig.date), "EEE")})` : ""}
                      </div>
                    </>
                  ) : (
                    <div>{journeyInfo.duration_text}</div>
                  )}
                  <button
                    type="button"
                    onClick={toggleBuffer}
                    style={{
                      marginTop: 3,
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      color: applyBuffer ? dPal.amber : dPal.textMuted,
                      fontSize: 12,
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                    }}
                    aria-label={applyBuffer ? "Buffer applied — click to remove" : "Buffer off — click to apply"}
                  >
                    {applyBuffer
                      ? `+ ${journeyInfo.buffer_minutes || 30} min buffer ✓`
                      : `+ ${journeyInfo.buffer_minutes || 30} min buffer ✕`}
                  </button>
                </div>
              </>
            ) : journeyInfo?.error ? (
              <div style={{ fontSize: 12, color: dPal.textDim }}>{journeyInfo.error}</div>
            ) : (
              <div style={{ fontSize: 12, color: dPal.textDim }}>
                {gig.call_time ? "Set home address in Settings" : "Add a call time to calculate"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── 5. Dress code + Notes + Share brief + Gig contacts (2x2) ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          borderBottom: dBorder.hairline,
          background: dPal.cardBg,
        }}
      >
        {/* Dress code — inline when short, dropdown only when too long for one line (mirrors Notes) */}
        <div style={{ padding: "20px 32px", minWidth: 0, borderBottom: dBorder.hairline }}>
          {editingField === "dress_code" ? (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <Kicker size={12} color={dPal.textDim} style={{ flexShrink: 0 }}>Dress code</Kicker>
              <Input
                value={editValues.dress_code}
                onChange={(e) => setEditValues({ ...editValues, dress_code: e.target.value })}
                placeholder="e.g., Black tie"
                autoFocus
                disabled={isSaving}
                {...inlineEditHandlers("dress_code")}
                style={{
                  flex: 1,
                  minWidth: 0,
                  ...dType.body,
                  color: dPal.text,
                  backgroundColor: dPal.innerBg,
                  borderColor: dPal.innerBorder,
                  fontFamily: dFont.body,
                }}
              />
            </div>
          ) : dressFitInline ? (
            <div
              onClick={() => setEditingField("dress_code")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setEditingField("dress_code");
                }
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = dPal.innerBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                cursor: "pointer",
                padding: "6px 12px",
                margin: "-6px -12px",
                borderRadius: dRad.sm,
                outline: "none",
                transition: `background ${tokens.motion.fast}`,
              }}
            >
              <Kicker size={12} color={dPal.textDim} style={{ flexShrink: 0 }}>Dress code</Kicker>
              <span
                style={{
                  ...dType.body,
                  color: gig.dress_code ? dPal.text : dPal.textMuted,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
              >
                {gig.dress_code || "Add dress code…"}
              </span>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setDressCodeOpen((v) => !v)}
                aria-expanded={dressCodeOpen}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                }}
              >
                <Kicker size={12} color={dPal.textDim} style={{ flexShrink: 0 }}>Dress code</Kicker>
                {!dressCodeOpen && (
                  <span style={{
                    flex: 1, minWidth: 0, fontSize: 13, color: dPal.text,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {gig.dress_code}
                  </span>
                )}
                <ChevronDown
                  className="h-4 w-4"
                  style={{
                    color: dPal.textDim,
                    flexShrink: 0,
                    marginLeft: "auto",
                    transform: dressCodeOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: `transform ${tokens.motion.fast}`,
                  }}
                />
              </button>

              {dressCodeOpen && (
                <div
                  onClick={() => setEditingField("dress_code")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setEditingField("dress_code");
                    }
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = dPal.innerBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  style={{
                    marginTop: 14,
                    cursor: "pointer",
                    padding: "8px 12px",
                    marginLeft: -12,
                    marginRight: -12,
                    borderRadius: dRad.sm,
                    outline: "none",
                    transition: `background ${tokens.motion.fast}`,
                  }}
                >
                  <p style={{ margin: 0, ...dType.body, lineHeight: 1.6, color: dPal.text, textWrap: "pretty" }}>
                    {gig.dress_code}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Notes — inline when short, dropdown only when too long for one line */}
        <div style={{ padding: "20px 32px", minWidth: 0, borderLeft: dBorder.hairline, borderBottom: dBorder.hairline }}>
          {editingField === "gig_notes" ? (
            <>
              <Kicker size={12} color={dPal.textDim}>Notes</Kicker>
              <Textarea
                value={editValues.gig_notes}
                onChange={(e) => setEditValues({ ...editValues, gig_notes: e.target.value })}
                placeholder="Add gig notes…"
                autoFocus
                disabled={isSaving}
                {...inlineEditHandlers("gig_notes")}
                style={{
                  marginTop: 14,
                  fontSize: 14.5,
                  lineHeight: 1.55,
                  minHeight: 120,
                  fontFamily: dFont.body,
                  backgroundColor: dPal.innerBg,
                  borderColor: dPal.innerBorder,
                  color: dPal.text,
                }}
              />
            </>
          ) : notesFitInline ? (
            <div
              onClick={() => setEditingField("gig_notes")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setEditingField("gig_notes");
                }
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = dPal.innerBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                cursor: "pointer",
                padding: "6px 12px",
                margin: "-6px -12px",
                borderRadius: dRad.sm,
                outline: "none",
                transition: `background ${tokens.motion.fast}`,
              }}
            >
              <Kicker size={12} color={dPal.textDim} style={{ flexShrink: 0 }}>Notes</Kicker>
              <span
                style={{
                  ...dType.body,
                  color: notesOf(gig) ? dPal.text : dPal.textMuted,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
              >
                {notesOf(gig) || "Add notes…"}
              </span>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setNotesOpen((v) => !v)}
                aria-expanded={notesOpen}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                }}
              >
                <Kicker size={12} color={dPal.textDim} style={{ flexShrink: 0 }}>Notes</Kicker>
                {!notesOpen && (
                  <span style={{
                    flex: 1, minWidth: 0, fontSize: 13, color: dPal.text,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {notesOf(gig)}
                  </span>
                )}
                <ChevronDown
                  className="h-4 w-4"
                  style={{
                    color: dPal.textDim,
                    flexShrink: 0,
                    marginLeft: "auto",
                    transform: notesOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: `transform ${tokens.motion.fast}`,
                  }}
                />
              </button>

              {notesOpen && (
                <div
                  onClick={() => setEditingField("gig_notes")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setEditingField("gig_notes");
                    }
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = dPal.innerBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  style={{
                    marginTop: 14,
                    cursor: "pointer",
                    padding: "8px 12px",
                    marginLeft: -12,
                    marginRight: -12,
                    borderRadius: dRad.sm,
                    outline: "none",
                    transition: `background ${tokens.motion.fast}`,
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      ...dType.body,
                      lineHeight: 1.6,
                      color: dPal.text,
                      textWrap: "pretty",
                    }}
                  >
                    {notesOf(gig)}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Share — pressing a button means you want to share, so there is no on/off switch */}
        <div style={{ padding: "20px 32px", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Share2 className="h-4 w-4" style={{ color: dPal.orange }} />
            <span style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap" }}>
              Share
            </span>
          </div>

          {gig?.gig_id && (
            <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <ShareBtn icon={MessageCircle} label="Dep this out" onClick={() => setShareKind("dep")} primary />
              <ShareBtn icon={MessageCircle} label="Share gig"    onClick={() => setShareKind("family")} />
              {shareEnabled && briefShareUrl && (
                <ShareBtn icon={LinkIcon} label="Copy link" onClick={() => handleShare("copy")} />
              )}
            </div>
          )}
          <GigShareDialog
            gig={{ ...gig, share_enabled: shareEnabled, share_token: shareToken }}
            kind={shareKind || "family"}
            open={!!shareKind}
            onClose={() => setShareKind(null)}
            onPublicEnabled={(token) => { setShareEnabled(true); setShareToken(token); }}
          />
          {shareEnabled && gig?.gig_id && (
            <div style={{ marginTop: 10, fontSize: 12, color: dPal.textMuted }}>
              Public link is on ·{" "}
              <button
                type="button"
                onClick={handleShareToggle}
                disabled={shareToggling}
                data-testid="stop-sharing"
                style={{ background: "none", border: "none", padding: 0, color: dPal.orange, fontSize: 12, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}
              >
                Stop sharing
              </button>
            </div>
          )}
          <ContactSheet contact={activeContact} onClose={() => setActiveContact(null)} />
        </div>

        {/* Gig contacts — inline names when few, dropdown only when too many for one line */}
        <div style={{ padding: "20px 32px", minWidth: 0, borderLeft: dBorder.hairline, borderTop: dBorder.hairline }}>
          {gigCollaborators.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Users className="h-4 w-4" style={{ color: dPal.orange }} />
                <Kicker size={12} color={dPal.textDim}>Gig contacts</Kicker>
              </div>
              <span style={{ fontSize: 13, color: dPal.textMuted }}>None tagged</span>
            </div>
          ) : contactsFitInline ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <Users className="h-4 w-4" style={{ color: dPal.orange }} />
                <Kicker size={12} color={dPal.textDim}>Gig contacts</Kicker>
              </div>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", gap: 12, minWidth: 0 }}>
                {gigCollaborators.map((c, i) => (
                  <div key={c.collaborator_id || i} role="button" tabIndex={0} onClick={() => setActiveContact(c)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveContact(c); } }} data-testid={`gig-contact-${c.collaborator_id}`} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, cursor: "pointer" }}>
                    <Avatar name={c.name} idx={i} size={24} ring={dPal.cardBg} />
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: dPal.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                      {c.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setContactsOpen((v) => !v)}
                aria-expanded={contactsOpen}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Users className="h-4 w-4" style={{ color: dPal.orange }} />
                  <Kicker size={12} color={dPal.textDim}>Gig contacts</Kicker>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {!contactsOpen && (
                    <div style={{ display: "inline-flex", alignItems: "center" }}>
                      {gigCollaborators.slice(0, 4).map((c, i) => (
                        <div key={c.collaborator_id || i} style={{ marginLeft: i === 0 ? 0 : -8 }}>
                          <Avatar name={c.name} idx={i} size={24} ring={dPal.cardBg} />
                        </div>
                      ))}
                    </div>
                  )}
                  <ChevronDown
                    className="h-4 w-4"
                    style={{
                      color: dPal.textDim,
                      flexShrink: 0,
                      transform: contactsOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: `transform ${tokens.motion.fast}`,
                    }}
                  />
                </div>
              </button>

              {contactsOpen && (
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                  {gigCollaborators.map((c, i) => (
                    <div key={c.collaborator_id || i} role="button" tabIndex={0} onClick={() => setActiveContact(c)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveContact(c); } }} data-testid={`gig-contact-${c.collaborator_id}`} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                      <Avatar name={c.name} idx={i} size={30} ring={dPal.cardBg} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: dPal.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {c.name}
                        </div>
                        {c.instrument && (
                          <div style={{ fontSize: 11.5, color: dPal.textMuted, marginTop: 1 }}>{c.instrument}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── 6. Trip done — multi-day only; ends the next-action tracker ──── */}
      {gig.is_multiday && hasPerformanceStarted() && (
      <div
        style={{
          padding: "16px 32px",
          borderBottom: dBorder.hairline,
          background: dPal.appBg,
        }}
      >
        <button
          onClick={toggleTripDone}
          disabled={finishingTrip}
          style={{
            width: "100%",
            padding: "12px 16px",
            background: gig.gig_finished ? dPal.green : dPal.innerBg,
            color: gig.gig_finished ? dPal.onGreen : dPal.text,
            border: "none",
            borderRadius: dRad.md,
            fontFamily: dFont.body,
            fontSize: 14,
            fontWeight: 600,
            cursor: finishingTrip ? "default" : "pointer",
            transition: `background ${tokens.motion.fast}, color ${tokens.motion.fast}`,
          }}
        >
          {finishingTrip ? "Saving…" : gig.gig_finished ? "✓ Trip done" : "Mark trip done"}
        </button>
      </div>
      )}

      <style>{`
        @keyframes dpulse {
          0%, 100% { opacity: 0.85; }
          50%      { opacity: 0.2; }
        }
      `}</style>
    </div>
  );
};

function ShareBtn({ icon: Icon, label, onClick, primary }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 14px",
        background: primary ? dPal.orange : "transparent",
        color: primary ? dPal.onOrange : dPal.text,
        border: primary ? `1px solid ${dPal.orange}` : `1px solid ${dPal.cardBorder}`,
        borderRadius: dRad.md,
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

// ───── EditableStatCell ──────────────────────────────────────────────
// A click-to-edit StatTile equivalent for the time strip. Displays
// label + value in the same visual rhythm as <StatTile>; on click,
// becomes an autoFocused input. Blur saves; Esc cancels (via the
// inlineEditHandlers closure from the parent).
function EditableStatCell({
  label,
  field,
  type,
  displayValue,
  valueColor,
  editingField,
  editValues,
  setEditValues,
  isSaving,
  inlineEditHandlers,
  onStartEdit,
}) {
  const isEditing = editingField === field;
  const tileStyle = {
    padding: 16,
    background: dPal.cardBg,
    border: dBorder.hairline,
    borderRadius: dRad.md,
    minWidth: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  };
  const valueStyle = {
    marginTop: 10,
    fontFamily: dFont.body,
    fontWeight: 600,
    fontSize: 28,
    letterSpacing: "-0.02em",
    color: valueColor || dPal.text,
  };

  if (isEditing) {
    return (
      <div style={tileStyle}>
        <Kicker size={11} color={dPal.textDim}>{label}</Kicker>
        <input
          type={type}
          value={editValues[field] ?? ""}
          autoFocus
          disabled={isSaving}
          onChange={(e) => setEditValues({ ...editValues, [field]: e.target.value })}
          {...inlineEditHandlers(field)}
          style={{
            ...valueStyle,
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            background: dPal.innerBg,
            border: dBorder.inner,
            borderRadius: dRad.sm,
            padding: "2px 8px",
            outline: "none",
          }}
        />
      </div>
    );
  }

  return (
    <div
      onClick={onStartEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onStartEdit();
        }
      }}
      style={{ ...tileStyle, cursor: "pointer", outline: "none" }}
    >
      <Kicker size={11} color={dPal.textDim}>{label}</Kicker>
      <div
        style={{
          ...valueStyle,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {displayValue}
      </div>
    </div>
  );
}

export default GigBriefDesktop;
